import React, { useState } from "react";
import { motion } from "framer-motion";
import { Mail, MapPin, Clock, Send, Linkedin, Twitter, Instagram } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/i18n/LanguageContext";
import { CONTACT } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-lg border border-white/10 bg-white/[0.02] px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none transition focus:border-[#00F0FF]/60 focus:ring-2 focus:ring-[#00F0FF]/20";

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        message: form.message.trim(),
      });
      toast.success(t.contact.form.success);
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      toast.error(t.contact.form.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid={CONTACT.root}
      className="relative py-24 md:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#00F0FF]">
              {t.contact.eyebrow}
            </span>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tighter text-white">
              {t.contact.title}
            </h2>
            <p className="mt-5 text-base text-[#8B95A5] leading-relaxed">{t.contact.sub}</p>

            <div className="mt-10 space-y-4">
              {[
                { Icon: Mail, label: t.contact.info.email_label, value: t.contact.info.email, href: `mailto:${t.contact.info.email}` },
                { Icon: MapPin, label: t.contact.info.location_label, value: t.contact.info.location },
                { Icon: Clock, label: t.contact.info.hours_label, value: t.contact.info.hours },
              ].map(({ Icon, label, value, href }) => (
                <div key={label} className="flex items-start gap-4 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 bg-white/[0.03] text-[#00F0FF]">
                    <Icon className="h-4 w-4" />
                  </span>
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-white/40">{label}</p>
                    {href ? (
                      <a href={href} className="mt-1 block text-sm text-white hover:text-[#00F0FF] transition-colors">
                        {value}
                      </a>
                    ) : (
                      <p className="mt-1 text-sm text-white">{value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 flex items-center gap-3">
              {[Linkedin, Twitter, Instagram].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  data-testid={`contact-social-${i}`}
                  aria-label="social"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/[0.02] text-white/70 hover:text-[#00F0FF] hover:border-[#00F0FF]/40 transition-all"
                >
                  <Icon className="h-3.5 w-3.5" />
                </a>
              ))}
            </div>
          </div>

          <motion.form
            data-testid={CONTACT.form}
            onSubmit={onSubmit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 glass-card rounded-3xl p-6 md:p-10"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-white/60 mb-1.5 block">{t.contact.form.name}</label>
                <input
                  required
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  data-testid={CONTACT.name}
                  className={inputCls}
                  placeholder="Jane Doe"
                />
              </div>
              <div>
                <label className="text-xs text-white/60 mb-1.5 block">{t.contact.form.email}</label>
                <input
                  required
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  data-testid={CONTACT.email}
                  className={inputCls}
                  placeholder="jane@company.com"
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="text-xs text-white/60 mb-1.5 block">{t.contact.form.company}</label>
              <input
                name="company"
                value={form.company}
                onChange={onChange}
                data-testid={CONTACT.company}
                className={inputCls}
                placeholder="Company"
              />
            </div>
            <div className="mt-4">
              <label className="text-xs text-white/60 mb-1.5 block">{t.contact.form.message}</label>
              <textarea
                required
                name="message"
                value={form.message}
                onChange={onChange}
                data-testid={CONTACT.message}
                rows={5}
                className={`${inputCls} resize-none`}
                placeholder="We're exploring…"
              />
            </div>

            <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
              <p className="text-xs text-white/40">
                By submitting you agree to be contacted by our team.
              </p>
              <button
                type="submit"
                disabled={loading}
                data-testid={CONTACT.submit}
                className="inline-flex items-center gap-2 rounded-full btn-primary px-6 py-3 text-sm font-semibold disabled:opacity-60"
              >
                {loading ? t.contact.form.sending : t.contact.form.submit}
                {!loading && <Send className="h-3.5 w-3.5" />}
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
